import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./HaloPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Halo" (/halo) — an ONBOARDING paywall, built to the standing #paywalls
// directive: SECURITY & PRIVACY.
//
// The hero is a keepsake MEDALLION holding a still mosaic of your memories. It
// arrives with its guard ring OPEN — a warm cameo anyone could pick up. The one
// mechanic no other paywall here uses: you DRAW THE GUARD RING CLOSED around it.
// As the luminous bead sweeps the rim, four real protections SEAT one by one —
// Only you · Encrypted · Private · In sync — and the ring completes into a solid
// halo. The register is a keepsake being sealed, not a fortress being locked:
// commitment + progress-completion, warm and calm.
//
// This is deliberately NOT the Aurora Vault "memory tiles orbiting a core in a
// galaxy" (passive, cosmic wonder). Here the memories sit still inside a warm
// medallion and YOU close the ring — an active custody gesture, not a drift.
// Not a reveal-bloom, lock, dial, keypad, door, window, wall or seal-stamp.
//
// Honest & App-Store compliant: the featured, most-prominent price is the YEARLY
// plan (7-day free trial, per-month broken out, honest Save %); monthly is a
// small secondary line. No scarcity / countdown. No data-loss claim — sealing
// only decides who can OPEN it; nothing is ever deleted or locked away from you,
// you hold the only key, Premium only ADDS protection. Reuses only the house
// font, colour tokens and the bundled byheart memory photos. Full light + dark,
// reduced-motion aware. Renders standalone on debug data; no native injection.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The memories kept inside the medallion — a still 2×2 cameo. Reused bundled
// byheart photos: the only assets this paywall pulls in.
const MEMORIES = ["m05", "m11", "m02", "m08"].map(photo)

// The four REAL Premium protections that seat as the ring closes, in order.
type Guard = { id: string; label: string; sub: string; icon: JSX.Element }
const GUARDS: Guard[] = [
  {
    id: "only-you",
    label: "Only you",
    sub: "Face ID / PIN",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M7.6 10V7.7a4.4 4.4 0 0 1 8.8 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect
          x="5.2"
          y="10"
          width="13.6"
          height="9.4"
          rx="2.6"
          fill="currentColor"
        />
        <circle cx="12" cy="14.3" r="1.35" fill="var(--hl-hole)" />
      </svg>
    ),
  },
  {
    id: "encrypted",
    label: "Encrypted",
    sub: "End-to-end",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M12 3.3l6.8 2.9v4.9c0 4.5-2.9 7.4-6.8 8.8-3.9-1.4-6.8-4.3-6.8-8.8V6.2L12 3.3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M8.8 12.1l2.1 2.1 4.3-4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "private",
    label: "Private",
    sub: "Off Camera Roll",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M2.6 12S6 6 12 6s9.4 6 9.4 6-3.4 6-9.4 6-9.4-6-9.4-6z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="2.5" fill="currentColor" />
        <path
          d="M4 4l16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "in-sync",
    label: "In sync",
    sub: "Your devices",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M5 12a7 7 0 0 1 11.9-5M19 12A7 7 0 0 1 7.1 17"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M16.4 3.6V7h-3.4M7.6 20.4V17h3.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
]

// Ring geometry (SVG units).
const CENTER = 120
const RADIUS = 104
const CIRC = 2 * Math.PI * RADIUS
// Each guard seats at the mid-point of its quarter of the ring, so the loop
// completes just after the last one seats.
const GUARD_AT = [0.14, 0.39, 0.64, 0.89]

function pointOnRing(progress: number) {
  const theta = (-90 + progress * 360) * (Math.PI / 180)
  return {
    x: CENTER + RADIUS * Math.cos(theta),
    y: CENTER + RADIUS * Math.sin(theta),
  }
}

function HaloPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const darkMode = state.initialData?.darkMode ?? false
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // progress 0 → 1 is how far the guard ring is drawn closed.
  const [progress, setProgress] = useState(reduce ? 1 : 0)
  const [dragging, setDragging] = useState(false)
  const [settle, setSettle] = useState(false)

  const stageRef = useRef<HTMLDivElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const progressRef = useRef(progress)
  progressRef.current = progress

  const sealed = progress > 0.995
  const seated = GUARD_AT.filter((g) => progress >= g - 0.001).length

  useEffect(() => {
    actions.setHeaderInsetColor(darkMode ? "#12141c" : "#f6f1e8")
    actions.setFooterInsetColor(darkMode ? "#14161f" : "#fffdf9")
  }, [actions, darkMode])

  const cancelRaf = () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
  }

  // Animate progress toward a target with ease-out; fire the settle pulse when
  // it lands fully sealed.
  const animateTo = useCallback(
    (target: number, duration: number) => {
      cancelRaf()
      const from = progressRef.current
      const delta = target - from
      if (Math.abs(delta) < 0.001) {
        setProgress(target)
        if (target > 0.995) pulseSettle()
        return
      }
      let start: number | null = null
      const step = (t: number) => {
        if (start == null) start = t
        const p = Math.min(1, (t - start) / duration)
        const eased = 1 - Math.pow(1 - p, 3)
        setProgress(from + delta * eased)
        if (p < 1) {
          rafRef.current = requestAnimationFrame(step)
        } else {
          rafRef.current = null
          if (target > 0.995) pulseSettle()
        }
      }
      rafRef.current = requestAnimationFrame(step)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const pulseSettle = () => {
    setSettle(true)
    window.setTimeout(() => setSettle(false), 620)
  }

  // Auto-seal a beat after load so the payoff reads in ~3s; then it stays
  // interactive (drag the bead to re-open / re-seal). Reduced motion arrives
  // sealed with no animation.
  useEffect(() => {
    if (reduce) {
      setProgress(1)
      return
    }
    const t = window.setTimeout(() => animateTo(1, 1550), 650)
    return () => {
      window.clearTimeout(t)
      cancelRaf()
    }
  }, [reduce, animateTo])

  const progressFromEvent = (clientX: number, clientY: number) => {
    const stage = stageRef.current
    if (!stage) return null
    const r = stage.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    // Angle measured clockwise from the top of the ring, normalised to 0..1.
    let deg = Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI) + 90
    if (deg < 0) deg += 360
    return deg / 360
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (reduce) return
    cancelRaf()
    setDragging(true)
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    const p = progressFromEvent(e.clientX, e.clientY)
    if (p != null) setProgress(p)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return
    const p = progressFromEvent(e.clientX, e.clientY)
    if (p == null) return
    // Reject wrap-around jumps so a drag past the top doesn't snap 1 → 0.
    if (Math.abs(p - progressRef.current) > 0.5) return
    setProgress(p)
  }

  const endDrag = () => {
    if (!dragging) return
    setDragging(false)
    // Commit: past halfway seals it, otherwise it eases back open.
    animateTo(progressRef.current >= 0.5 ? 1 : 0, 420)
  }

  // Tapping a guard chip seats up to that protection.
  const seatUpTo = (i: number) => {
    if (reduce) return
    animateTo(i === GUARDS.length - 1 ? 1 : GUARD_AT[i] + 0.02, 520)
  }

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    native.logMixpanelEvent("html_halo_purchase", { plan })
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const ctaLabel =
    plan === "yearly" && yearly?.hasTrial
      ? "Start 7-day free trial"
      : "Seal my vault"

  const ctaSub =
    plan === "yearly"
      ? yearly?.hasTrial
        ? `Then ${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
        : `${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  const bead = pointOnRing(progress)

  return (
    <div
      className={`${styles.root} ${darkMode ? styles.isDark : ""} ${
        sealed ? styles.sealed : ""
      } ${settle ? styles.settling : ""}`}
    >
      <div className={styles.aura} aria-hidden />
      <div className={styles.grain} aria-hidden />

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className={styles.scrim} style={{ paddingTop: topInset }}>
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M8 10V7.7a4 4 0 0 1 8 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.6"
                fill="currentColor"
              />
              <circle cx="12" cy="14.2" r="1.35" fill="var(--hl-brand-hole)" />
            </svg>
            <span>
              Private Photo Vault <b>Premium</b>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Not now"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <header className={styles.hero}>
          <span className={styles.kicker}>Set up your private vault</span>
          <h1 className={styles.title}>
            Draw the ring closed.
            <br />
            Only you can open it.
          </h1>
          <p className={styles.sub}>
            Seal your memories inside a keepsake only your Face&nbsp;ID or PIN
            opens — private the moment the ring completes.
          </p>
        </header>
      </div>

      {/* ── The medallion + guard ring ──────────────────────────────────── */}
      <div className={styles.stage}>
        <div
          className={styles.dial}
          ref={stageRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          role="slider"
          aria-label="Draw the protection ring closed"
          aria-valuemin={0}
          aria-valuemax={4}
          aria-valuenow={seated}
        >
          <svg className={styles.ring} viewBox="0 0 240 240" aria-hidden>
            <defs>
              <linearGradient id="hl-arc" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="var(--hl-arc-1)" />
                <stop offset="1" stopColor="var(--hl-arc-2)" />
              </linearGradient>
            </defs>
            {/* Open track */}
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              className={styles.track}
              fill="none"
            />
            {/* Sealed arc, drawn clockwise from the top */}
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              className={styles.arc}
              fill="none"
              stroke="url(#hl-arc)"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={CIRC * (1 - progress)}
              transform={`rotate(-90 ${CENTER} ${CENTER})`}
            />
            {/* Guard pips */}
            {GUARD_AT.map((g, i) => {
              const pt = pointOnRing(g)
              const on = progress >= g - 0.001
              return (
                <g
                  key={GUARDS[i].id}
                  className={`${styles.pip} ${on ? styles.pipOn : ""}`}
                >
                  <circle
                    cx={pt.x}
                    cy={pt.y}
                    r="12"
                    className={styles.pipDisc}
                  />
                </g>
              )
            })}
          </svg>

          {/* Guard icons layered over their pips (HTML for crisp currentColor) */}
          {GUARD_AT.map((g, i) => {
            const pt = pointOnRing(g)
            const on = progress >= g - 0.001
            return (
              <span
                key={GUARDS[i].id}
                className={`${styles.pipIcon} ${on ? styles.pipIconOn : ""}`}
                style={{
                  left: `${(pt.x / 240) * 100}%`,
                  top: `${(pt.y / 240) * 100}%`,
                }}
              >
                {GUARDS[i].icon}
              </span>
            )
          })}

          {/* The bead you draw around the rim */}
          <span
            className={styles.bead}
            style={{
              left: `${(bead.x / 240) * 100}%`,
              top: `${(bead.y / 240) * 100}%`,
            }}
            aria-hidden
          />

          {/* The keepsake medallion */}
          <div className={styles.medallion}>
            <div className={styles.mosaic}>
              {MEMORIES.map((src) => (
                <span
                  key={src}
                  className={styles.tile}
                  style={{ backgroundImage: `url(${src})` }}
                />
              ))}
            </div>
            <div className={styles.core} aria-hidden>
              <svg viewBox="0 0 24 24" width="26" height="26">
                <path
                  d="M8 10V7.7a4 4 0 0 1 8 0V10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="5.4"
                  y="10"
                  width="13.2"
                  height="9.6"
                  rx="2.8"
                  fill="currentColor"
                />
                <circle cx="12" cy="14.4" r="1.5" fill="var(--hl-core-hole)" />
              </svg>
            </div>
          </div>
        </div>

        <div className={styles.caption} aria-live="polite">
          {sealed ? (
            <span>
              <i className={styles.capDot} data-on /> Sealed — kept for only you
            </span>
          ) : (
            <span>
              <i className={styles.capDot} /> {seated}/4 on — draw the ring
              closed
            </span>
          )}
        </div>
      </div>

      {/* ── Pricing + CTA (yearly featured) ─────────────────────────────── */}
      <div className={styles.panel} style={{ paddingBottom: bottomInset + 22 }}>
        <div className={styles.legend}>
          {GUARDS.map((h, i) => {
            const on = progress >= GUARD_AT[i] - 0.001
            return (
              <button
                key={h.id}
                type="button"
                className={`${styles.chip} ${on ? styles.chipOn : ""}`}
                onClick={() => seatUpTo(i)}
                title={h.sub}
              >
                <span className={styles.chipIcon}>{h.icon}</span>
                <span className={styles.chipLabel}>{h.label}</span>
              </button>
            )
          })}
        </div>

        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.planOn : ""}`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.radio} data-on={plan === "yearly"} />
            <span className={styles.planCopy}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                {yearly?.hasTrial
                  ? "7-day free trial, then billed yearly"
                  : "billed yearly"}
              </span>
            </span>
            <span className={styles.priceCol}>
              <span className={styles.priceRow}>
                <b className={styles.priceBig}>{yearlyPrice}</b>
                <span className={styles.pricePer}>/yr</span>
              </span>
              <span className={styles.pricePm}>{perMonth}/mo</span>
            </span>
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.planOn : ""}`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.radio} data-on={plan === "monthly"} />
            <span className={styles.planNameSm}>Monthly</span>
            <span className={styles.priceSm}>
              {monthlyPrice}
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </div>

        <button className={styles.cta} onClick={purchase}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <path
              d="M5 12h13M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className={styles.ctaSub}>{ctaSub}</p>
        <p className={styles.reassure}>
          Sealing only decides who can open your vault — your memories are
          always yours. Nothing is ever deleted or locked away; you hold the
          only key, and Premium only adds protection.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default HaloPaywall
